import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaste } from "@fortawesome/pro-light-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { motion } from "framer-motion";
import { useDarkMode } from "../../hooks";
import { copyToClipboard, getFontAwesomeIcon } from "../../utils";
import { opacityScaleVariants } from "../../motionVariants";

interface ClipBoardProps {
	text: string;
	onCopy: () => void;
	copied: boolean;
	icon?: IconProp;
	additionalClasses?: string;
	withText?: boolean;
	copyText?: string;
	hasIconBorder?: boolean;
	onClick?: () => void;
}

const CopyToClipboardIcon: React.FC<ClipBoardProps> = ({
	text = "default",
	onCopy,
	icon = faPaste,
	additionalClasses,
	withText = false,
	copyText = "Copy",
	hasIconBorder = true,
	onClick = () => {},
}) => {
	const { darkMode } = useDarkMode();
	const [isClicked, setIsClicked] = useState(false);
	const [isCopied, setIsCopied] = useState<boolean>(false);
	const handleCopy = async (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		handleClick();

		if (onClick) {
			onClick();
		}

		await copyToClipboard(text, onCopy, setIsCopied);
	};

	const handleClick = () => {
		setIsClicked(true);
		setTimeout(() => setIsClicked(false), 1300);
	};
	const greenCheckMarkIconStyles = darkMode
		? "text-accent bg-gray-400"
		: "text-green-500 bg-green-white";
	const isContainerStyles = `${greenCheckMarkIconStyles} h-6 w-6 z-20 rounded-full border-1 border-green-500 bg-green-100 p-1 flex justify-center items-center`;
	const notCopiedIconBackgroundStyles = " hover:bg-primaryVeryLight bg-white";
	const notCopiedIconBackgroundContainerStyles = `${additionalClasses} ${notCopiedIconBackgroundStyles} text-primary rounded-full h-4 w-4 p-1`;
	const withTextCopyIconContainerStyles = " flex text-primary ";
	const isCopiedWithTextContainer = darkMode
		? "text-accent bg-gray-400"
		: "text-green-500";
	const rootContainerStyles =
		"ml-2 relative cursor-pointer rounded flex items-center";

	const withTextContainerStyles = "flex items-center z-20";
	// const copiedText = "Copied";
	return (
		<div className={rootContainerStyles}>
			{withText ? (
				<div
					className={withTextContainerStyles}
					onClick={(e) => handleCopy(e)}
				>
					{isCopied ? (
						<div className={isCopiedWithTextContainer}>
							{getFontAwesomeIcon("check", "regular")}
							<span className="pl-2">{copyText}</span>
						</div>
					) : (
						<>
							<div className={withTextCopyIconContainerStyles}>
								<div
									className={`hover:bg-supply-blue-50 flex justify-center items-center size-5 relative  rounded-full border-supply-blue-500 ${
										hasIconBorder ? "border-1" : ""
									}`}
								>
									<div className=" text-xs">
										{getFontAwesomeIcon("copy", "light")}
									</div>
								</div>
								<span className="pl-2">{copyText}</span>
							</div>
						</>
					)}
				</div>
			) : (
				<>
					<div className="h-7" onClick={(e) => handleCopy(e)}>
						<motion.div
							initial={opacityScaleVariants.initial}
							animate={opacityScaleVariants.animate(isClicked)}
							transition={opacityScaleVariants.transition}
							onClick={handleClick}
						>
							{!isCopied && (
								<FontAwesomeIcon
									icon={icon}
									className={
										notCopiedIconBackgroundContainerStyles
									}
								/>
							)}
						</motion.div>

						{isCopied && (
							<motion.div
								initial={opacityScaleVariants.initial}
								animate={opacityScaleVariants.animate(
									isClicked
								)}
								transition={opacityScaleVariants.transition}
							>
								<div className={isContainerStyles}>
									{getFontAwesomeIcon("check", "light")}
								</div>
							</motion.div>
						)}
					</div>
				</>
			)}
		</div>
	);
};

export default CopyToClipboardIcon;
